import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
    Users,
    UserCheck,
    UserX,
    FileText,
    Inbox,
    Activity,
    AlertTriangle,
    TrendingUp,
    Camera,
    FolderKanban,
    Printer,
    Loader2,
    ArrowRight,
    Phone,
    KeyRound,
    Image as ImageIcon,
} from "lucide-react";
import { ComplianceWidget } from "../components/ComplianceWidget";
import { EmployeeNotificationsWidget } from "../components/EmployeeNotificationsWidget";
import { DocumentsPendingAckWidget } from "../components/DocumentsPendingAckWidget";
import { ArchiveCoverageWidget } from "../components/ArchiveCoverageWidget";
import { PayslipCheckWidget } from "../components/PayslipCheckWidget";
import { ExpiredContractsWidget } from "../components/ExpiredContractsWidget";
import { toast } from "sonner";

const fmtNum = (n) => Number(n || 0).toLocaleString("it-IT");
const fmtBytes = (b) => {
    if (!b) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(b) / Math.log(k));
    return `${(b / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

export default function Overview() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [empRes, importRes, printRes, alertsRes] = await Promise.all([
                    api.get("/employees"),
                    api.get("/migration/import-stats").catch(() => ({ data: null })),
                    api.get("/print-events/stats").catch(() => ({ data: null })),
                    api.get("/documents/alerts/expiring", {
                        params: { days_documents: 90, days_contracts: 90 },
                    }).catch(() => ({ data: null })),
                ]);
                const emps = empRes.data || [];
                const active = emps.filter((e) => !e.data_cessazione);
                const cessati = emps.filter((e) => !!e.data_cessazione);
                const empMap = {};
                emps.forEach((e) => { empMap[e.id] = e; });

                // Build a unified list of all expiring/expired items (contracts + documents)
                const alerts = alertsRes.data || {};
                const all = [];
                (alerts.expired_contracts || []).forEach((c) =>
                    all.push({ kind: "contract", scope: "expired", ...c }),
                );
                (alerts.expired_documents || []).forEach((d) =>
                    all.push({ kind: "document", scope: "expired", ...d }),
                );
                (alerts.expiring_contracts || []).forEach((c) =>
                    all.push({ kind: "contract", scope: "expiring", ...c }),
                );
                (alerts.expiring_documents || []).forEach((d) =>
                    all.push({ kind: "document", scope: "expiring", ...d }),
                );
                all.sort((a, b) => a.days_left - b.days_left);

                setStats({
                    employees: emps.length,
                    active: active.length,
                    cessati: cessati.length,
                    importStats: importRes.data,
                    printStats: printRes.data,
                    expiring: all.slice(0, 6),
                    expiringTotal: all.length,
                    employeesMap: empMap,
                });
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="flex h-96 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="overview-page">
            <div className="mb-8">
                <h1 className="font-display text-3xl font-bold text-[#376DB3]">Buongiorno</h1>
                <p className="mt-1 text-sm text-slate-600">
                    Panoramica del tuo Portale HR — {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </p>
            </div>

            {/* KPI cards */}
            <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
                <KpiCard
                    icon={UserCheck}
                    label="Dipendenti attivi"
                    value={fmtNum(stats?.active)}
                    accent="emerald"
                    to="/dipendenti"
                    testId="kpi-active"
                />
                <KpiCard
                    icon={UserX}
                    label="Cessati"
                    value={fmtNum(stats?.cessati)}
                    accent="slate"
                    to="/dipendenti"
                    testId="kpi-cessati"
                />
                <KpiCard
                    icon={FileText}
                    label="Documenti totali"
                    value={fmtNum(stats?.importStats?.total_documents)}
                    sub={stats?.importStats ? fmtBytes(stats.importStats.total_size_bytes) : null}
                    accent="blue"
                    to="/migrazione/stato"
                    testId="kpi-docs"
                />
                <KpiCard
                    icon={Inbox}
                    label="In attesa"
                    value={fmtNum(stats?.importStats?.pending_documents)}
                    accent={(stats?.importStats?.pending_documents || 0) > 0 ? "amber" : "slate"}
                    to="/documenti/in-attesa"
                    testId="kpi-pending"
                />
            </div>

            {/* HR Compliance widget */}
            <div className="mb-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
                <div className="flex flex-col gap-6">
                    <ExpiredContractsWidget />
                    <ComplianceWidget />
                    <PayslipCheckWidget />
                </div>
                <div className="flex flex-col gap-6">
                    <EmployeeNotificationsWidget />
                    <DocumentsPendingAckWidget />
                    <ArchiveCoverageWidget />
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                {/* Quick actions */}
                <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                    <h2 className="mb-3 font-display text-base font-semibold text-slate-700">Azioni rapide</h2>
                    <div className="space-y-2">
                        <ActionLink to="/dipendenti" icon={Users}>Gestisci dipendenti</ActionLink>
                        <ActionLink to="/documenti/in-attesa" icon={Inbox}>Upload massivo documenti</ActionLink>
                        <ActionLink to="/foto/upload" icon={Camera}>Carica foto tesserini</ActionLink>
                        <ActionLink to="/gruppi" icon={FolderKanban}>Gestisci gruppi</ActionLink>
                        <ActionLink to="/storico-stampe" icon={Printer}>Storico stampe</ActionLink>
                        <ActionButton icon={Phone} onClick={async () => {
                            try {
                                const r = await api.get("/employees/missing-phones.csv", { responseType: "blob" });
                                const url = window.URL.createObjectURL(new Blob([r.data]));
                                const a = document.createElement("a");
                                a.href = url; a.download = "dipendenti_senza_telefono.csv";
                                document.body.appendChild(a); a.click(); a.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success("Report scaricato");
                            } catch (e) {
                                toast.error("Errore download");
                            }
                        }} testId="download-missing-phones">
                            Scarica dipendenti senza telefono
                        </ActionButton>
                        <ActionButton icon={KeyRound} onClick={async () => {
                            try {
                                const r = await api.get("/employees/missing-credentials.csv", { responseType: "blob" });
                                const url = window.URL.createObjectURL(new Blob([r.data]));
                                const a = document.createElement("a");
                                a.href = url; a.download = "dipendenti_senza_credenziali.csv";
                                document.body.appendChild(a); a.click(); a.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success("Report scaricato");
                            } catch (e) {
                                toast.error("Errore download");
                            }
                        }} testId="download-missing-credentials">
                            Scarica dipendenti senza credenziali
                        </ActionButton>
                        <ActionButton icon={ImageIcon} onClick={async () => {
                            try {
                                const r = await api.get("/photos/quality-audit.csv", { responseType: "blob" });
                                const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
                                const a = document.createElement("a");
                                a.href = url; a.download = "audit_foto_tesserini.csv";
                                document.body.appendChild(a); a.click(); a.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success("Audit foto scaricato");
                            } catch (e) {
                                toast.error("Errore download");
                            }
                        }} testId="download-photo-audit">
                            Audit qualità foto tesserini
                        </ActionButton>
                    </div>
                </section>

                {/* Recent activity / upload velocity */}
                <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                    <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-slate-700">
                        <TrendingUp className="h-4 w-4 text-[#376DB3]" />
                        Attività upload
                    </h2>
                    <div className="space-y-3 text-sm">
                        <StatRow label="Ultima ora" value={stats?.importStats?.uploads_last_1h} />
                        <StatRow label="Ultime 24h" value={stats?.importStats?.uploads_last_24h} />
                        <StatRow label="Ultimi 7 giorni" value={stats?.importStats?.uploads_last_7d} />
                    </div>
                    <div className="mt-4 border-t border-slate-100 pt-3">
                        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Stampe tesserini</h3>
                        <StatRow label="Operazioni totali" value={stats?.printStats?.total_events} />
                        <StatRow label="Tesserini stampati" value={stats?.printStats?.total_badges_printed} />
                    </div>
                </section>

                {/* Expiring contracts + documents — next 90 days */}
                <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="overview-scadenze-widget">
                    <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-slate-700">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        Scadenze prossime
                        {stats?.expiringTotal > 0 && (
                            <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                                {stats.expiringTotal}
                            </span>
                        )}
                    </h2>
                    <div className="space-y-2">
                        {(stats?.expiring || []).map((e, idx) => {
                            const emp = stats.employeesMap?.[e.employee_id];
                            const expired = e.days_left < 0;
                            const isContract = e.kind === "contract";
                            const label = isContract
                                ? (e.contratto_tipo || "Contratto TD")
                                : (e.filename || e.category || "Documento");
                            const date = e.scadenza || e.expires_at;
                            return (
                                <Link
                                    key={`${e.kind}-${e.id || e.employee_id}-${idx}`}
                                    to={`/dipendenti/${e.employee_id}`}
                                    className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm hover:opacity-90 ${
                                        expired
                                            ? "border-red-200 bg-red-50"
                                            : "border-amber-100 bg-amber-50 hover:bg-amber-100"
                                    }`}
                                    data-testid={`overview-scadenza-${idx}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate font-medium text-slate-700">
                                            {emp ? `${emp.cognome} ${emp.nome}` : "Dipendente"}
                                        </div>
                                        <div className="truncate text-xs text-slate-500" title={label}>
                                            {isContract ? "Contratto" : "Documento"} · {label} · {date?.split("-").reverse().join("/")}
                                            {" · "}
                                            <span className={expired ? "font-semibold text-red-700" : (e.days_left <= 30 ? "font-semibold text-amber-700" : "")}>
                                                {e.days_left < 0
                                                    ? `scaduto da ${Math.abs(e.days_left)} gg`
                                                    : e.days_left === 0
                                                      ? "scade oggi"
                                                      : e.days_left === 1
                                                        ? "scade domani"
                                                        : `tra ${e.days_left} gg`}
                                            </span>
                                        </div>
                                    </div>
                                    <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                                </Link>
                            );
                        })}
                        <div className={`py-6 text-center text-sm text-slate-400 ${(stats?.expiring?.length || 0) === 0 ? "" : "hidden"}`}>
                            ✓ Nessuna scadenza imminente
                        </div>
                        {stats?.expiringTotal > 0 && (
                            <Link
                                to="/scadenze"
                                className="block rounded-md border border-[#376DB3]/30 bg-[#376DB3]/5 px-3 py-2 text-center text-xs font-semibold text-[#376DB3] hover:bg-[#376DB3]/10"
                                data-testid="overview-scadenze-link"
                            >
                                Vedi tutte le scadenze →
                            </Link>
                        )}
                    </div>
                </section>
            </div>

            {/* Future modules teaser */}
            <div className="mt-8 rounded-lg border border-dashed border-slate-300 bg-gradient-to-br from-slate-50 to-white p-6">
                <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <Activity className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h3 className="font-display text-base font-semibold text-slate-700">Modulo Presenze</h3>
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">In arrivo</span>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                            Timbratura geolocalizzata dal portale dipendente, con verifica GPS e calcolo automatico delle ore lavorate. Disponibile come app installabile su smartphone.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

const KpiCard = ({ icon: Icon, label, value, sub, accent = "blue", to, testId }) => {
    const tones = {
        emerald: "bg-emerald-50 text-emerald-700",
        slate: "bg-slate-100 text-slate-600",
        blue: "bg-[#376DB3]/10 text-[#376DB3]",
        amber: "bg-amber-100 text-amber-700",
    };
    const valueTone = {
        emerald: "text-emerald-600",
        slate: "text-slate-700",
        blue: "text-[#376DB3]",
        amber: "text-amber-600",
    };
    const card = (
        <div className="flex h-full items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md" data-testid={testId}>
            <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md ${tones[accent]}`}>
                <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
                <div className="truncate text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
                <div className={`font-display text-2xl font-bold font-mono-numbers ${valueTone[accent]}`}>{value}</div>
                <div className={`truncate text-[11px] text-slate-500 ${sub ? "" : "hidden"}`}>{sub || ""}</div>
            </div>
        </div>
    );
    return to ? <Link to={to}>{card}</Link> : card;
};

const StatRow = ({ label, value }) => (
    <div className="flex items-center justify-between">
        <span className="text-slate-600">{label}</span>
        <span className="font-mono-numbers font-semibold text-[#376DB3]">{fmtNum(value)}</span>
    </div>
);

const ActionLink = ({ to, icon: Icon, children }) => (
    <Link to={to} className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
        <Icon className="h-4 w-4 text-[#376DB3]" />
        <span className="flex-1">{children}</span>
        <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
    </Link>
);

const ActionButton = ({ icon: Icon, onClick, children, testId }) => (
    <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
        data-testid={testId}
    >
        <Icon className="h-4 w-4 text-[#376DB3]" />
        <span className="flex-1">{children}</span>
        <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
    </button>
);
